import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, e as emitEvent, l as logDeprecatedEventOnce } from './utils.js';
import { d as defineCustomElement$3 } from './cl-icon2.js';
import { d as defineCustomElement$2 } from './cl-typography2.js';

// Constants
const component = 'cl-toast';
const toastConstants = {
  POSITION: {
    TOP: 'top',
    TOP_LEFT: 'top-left',
    TOP_RIGHT: 'top-right',
    BOTTOM: 'bottom',
    BOTTOM_LEFT: 'bottom-left',
    BOTTOM_RIGHT: 'bottom-right',
  },
  TRANSITION: {
    NONE: 'none',
    FADE: 'fade',
    SLIDE_RIGHT: 'slide-right',
    SLIDE_LEFT: 'slide-left',
    SLIDE_UP: 'slide-up',
    SLIDE_DOWN: 'slide-down',
  },
  TRANSITION_PHASE: {
    BASE: 'base',
    ACTIVE: 'active',
    DONE: 'done',
  },
  TRANSITION_STATE: {
    ENTERING: 'entering',
    ENTERED: 'entered',
    EXITING: 'exiting',
    EXITED: 'exited',
  },
  VARIANT: {
    INFO: 'info',
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error',
  },
};
// Type-validators
const toastValidator = {
  position: createTypeValidator(toastConstants.POSITION),
  transition: createTypeValidator(toastConstants.TRANSITION),
  variant: createTypeValidator(toastConstants.VARIANT),
};

const clToastCss = ".sc-cl-toast-h{--toast-z-index:var(--z-index-10)}.sc-cl-toast-h .sc-cl-toast-s>cl-avatar{border-radius:50%;height:var(--spacing-5);max-width:var(--spacing-5);min-width:var(--spacing-5)}.sc-cl-toast-h .sc-cl-toast-s>cl-avatar img{-o-object-fit:cover;object-fit:cover}.sc-cl-toast-h .sc-cl-toast-s>cl-thumbnail :hover{cursor:default}.sc-cl-toast-h .sc-cl-toast-s>cl-icon{--icon-size:var(--spacing-3);height:calc(2.5 * var(--spacing-1));margin-left:var(--spacing-1);max-width:calc(2.5 * var(--spacing-1));min-width:calc(2.5 * var(--spacing-1));padding:0}.sc-cl-toast-h .sc-cl-toast-s>cl-icon[slot=icon]:not(:first-child){display:none}.toast.sc-cl-toast-h{left:var(--spacing-1);position:fixed;right:var(--spacing-1)}@media (min-width: 600px){.toast--top.sc-cl-toast-h,.toast--bottom.sc-cl-toast-h{left:50%;right:auto;transform:translateX(-50%)}}.toast--top.sc-cl-toast-h{top:var(--spacing-1)}.toast--top-left.sc-cl-toast-h{left:var(--spacing-1);top:var(--spacing-1)}@media (min-width: 600px){.toast--top-left.sc-cl-toast-h{right:auto}}.toast--top-right.sc-cl-toast-h{right:var(--spacing-1);top:var(--spacing-1)}@media (min-width: 600px){.toast--top-right.sc-cl-toast-h{left:auto}}.toast--bottom.sc-cl-toast-h{bottom:var(--spacing-1)}.toast--bottom-left.sc-cl-toast-h{bottom:var(--spacing-1);left:var(--spacing-1)}@media (min-width: 600px){.toast--bottom-left.sc-cl-toast-h{right:auto}}.toast--bottom-right.sc-cl-toast-h{bottom:var(--spacing-1);right:var(--spacing-1)}@media (min-width: 600px){.toast--bottom-right.sc-cl-toast-h{left:auto}}.toast.sc-cl-toast-h:hover .toast__close.sc-cl-toast{visibility:visible}.toast--success.sc-cl-toast-h{color:rgba(var(--system-color-success-main))}.toast--warning.sc-cl-toast-h{color:rgba(var(--system-color-warning-main))}.toast--error.sc-cl-toast-h{color:rgba(var(--system-color-error-main))}.toast--info.sc-cl-toast-h{color:rgb(var(--system-color-info-cobalt-main))}.sc-cl-toast-h .toast__content.sc-cl-toast{background-color:rgba(var(--surface-color-base));border-radius:var(--border-radius-interactive-small);border-style:solid;border-width:var(--border-width);box-shadow:var(--elevation-3);box-sizing:border-box;display:flex;justify-content:flex-start;padding:var(--spacing-2);transition-duration:0.25s;transition-timing-function:cubic-bezier(0.82, 0.75, 0.8, 0.71)}.sc-cl-toast-h .toast__content--success.sc-cl-toast{border-color:rgba(var(--system-color-success-main))}.sc-cl-toast-h .toast__content--warning.sc-cl-toast{border-color:rgba(var(--system-color-warning-main))}.sc-cl-toast-h .toast__content--error.sc-cl-toast{border-color:rgba(var(--system-color-error-main))}.sc-cl-toast-h .toast__content--info.sc-cl-toast{border-color:rgba(var(--color-neutral-100))}.sc-cl-toast-h .toast__content--exited.sc-cl-toast{display:none}.sc-cl-toast-h .toast__content--fade.sc-cl-toast{transition-property:opacity}.sc-cl-toast-h .toast__content--fade.toast__content--entering-active.sc-cl-toast,.sc-cl-toast-h .toast__content--fade.toast__content--exiting-base.sc-cl-toast{opacity:1}.sc-cl-toast-h .toast__content--fade.toast__content--entering-base.sc-cl-toast,.sc-cl-toast-h .toast__content--fade.toast__content--exiting-active.sc-cl-toast,.sc-cl-toast-h .toast__content--fade.toast__content--exited-base.sc-cl-toast{opacity:0}.sc-cl-toast-h .toast__content--slide-right.sc-cl-toast{transition-property:transform}.sc-cl-toast-h .toast__content--slide-right.toast__content--entering-active.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-right.toast__content--exiting-base.sc-cl-toast{transform:translateX(0)}.sc-cl-toast-h .toast__content--slide-right.toast__content--entering-base.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-right.toast__content--exiting-active.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-right.toast__content--exited-base.sc-cl-toast{transform:translateX(calc(-100% - var(--spacing-1) - var(--border-width)))}.sc-cl-toast-h .toast__content--slide-left.sc-cl-toast{transition-property:transform}.sc-cl-toast-h .toast__content--slide-left.toast__content--entering-active.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-left.toast__content--exiting-base.sc-cl-toast{transform:translateX(0)}.sc-cl-toast-h .toast__content--slide-left.toast__content--entering-base.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-left.toast__content--exiting-active.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-left.toast__content--exited-base.sc-cl-toast{transform:translateX(calc(100% + var(--spacing-1) + var(--border-width)))}.sc-cl-toast-h .toast__content--slide-up.sc-cl-toast{transition-property:transform}.sc-cl-toast-h .toast__content--slide-up.toast__content--entering-active.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-up.toast__content--exiting-base.sc-cl-toast{transform:translateY(0)}.sc-cl-toast-h .toast__content--slide-up.toast__content--entering-base.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-up.toast__content--exiting-active.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-up.toast__content--exited-base.sc-cl-toast{transform:translateY(calc(100% + var(--spacing-1) + var(--border-width)))}.sc-cl-toast-h .toast__content--slide-down.sc-cl-toast{transition-property:transform}.sc-cl-toast-h .toast__content--slide-down.toast__content--entering-active.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-down.toast__content--exiting-base.sc-cl-toast{transform:translateY(0)}.sc-cl-toast-h .toast__content--slide-down.toast__content--entering-base.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-down.toast__content--exiting-active.sc-cl-toast,.sc-cl-toast-h .toast__content--slide-down.toast__content--exited-base.sc-cl-toast{transform:translateY(calc(-100% - var(--spacing-1) + var(--border-width)))}.sc-cl-toast-h .toast__container.sc-cl-toast{margin:0 var(--spacing-1) calc(var(--spacing-half) * -1);width:100%}.sc-cl-toast-h .toast__container--extra-spacing.sc-cl-toast{margin-left:var(--spacing-2)}.sc-cl-toast-h .toast__top.sc-cl-toast{display:flex;justify-content:space-between}.sc-cl-toast-h .toast__timestamp.sc-cl-toast{margin:0 0 0 auto;white-space:nowrap}.sc-cl-toast-h .toast__tag.sc-cl-toast{margin:0 0 var(--spacing-1)}.sc-cl-toast-h .toast__header.sc-cl-toast{--typography-weight:var(--font-weight-700);margin:0 0 var(--spacing-half) 0}.sc-cl-toast-h .toast__text.sc-cl-toast{margin:0 0 var(--spacing-half) 0;padding:0;word-break:break-word}@media (min-width: 600px){.sc-cl-toast-h .toast__text.sc-cl-toast{max-width:calc(3 * var(--spacing-10))}}.sc-cl-toast-h .toast__text.sc-cl-toast:only-child{display:inline-block;vertical-align:middle}.sc-cl-toast-h .toast__actions.sc-cl-toast{margin-top:var(--spacing-half)}.sc-cl-toast-h .toast__close.sc-cl-toast{color:rgba(var(--color-neutral-900));cursor:pointer;height:calc(2.5 * var(--spacing-1));min-width:calc(2.5 * var(--spacing-1));visibility:hidden}.toast--inline.sc-cl-toast-h{display:block;position:relative}.toast--inline.sc-cl-toast-h .toast__content.sc-cl-toast{box-shadow:none;max-width:-moz-fit-content;max-width:fit-content;position:relative}.toast--inline.sc-cl-toast-h .toast__content--info.sc-cl-toast{border:var(--border-width) solid rgb(var(--system-color-info-cobalt-main))}.toast--inline.sc-cl-toast-h .toast__close.sc-cl-toast{visibility:visible}.toast__actions.sc-cl-toast>*.sc-cl-toast{margin-bottom:0 !important;margin-top:0 !important}.toast__actions.sc-cl-toast a.sc-cl-toast{text-decoration:none}.toast__actions.sc-cl-toast a.sc-cl-toast:hover{text-decoration:underline}";

const ClToast$1 = /*@__PURE__*/ proxyCustomElement(class ClToast extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clDismiss = createEvent(this, "clDismiss", 7);
    this.clEnterStart = createEvent(this, "clEnterStart", 7);
    this.clEnterEnd = createEvent(this, "clEnterEnd", 7);
    this.clExitStart = createEvent(this, "clExitStart", 7);
    this.clExitEnd = createEvent(this, "clExitEnd", 7);
    this.dismiss = createEvent(this, "dismiss", 7);
    this.enterStart = createEvent(this, "enter", 7);
    this.enterEnd = createEvent(this, "entered", 7);
    this.exitStart = createEvent(this, "exit", 7);
    this.exitEnd = createEvent(this, "exited", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * The id of the set timeout for automatically dismissing after x amount of seconds.
     */
    this.durationTimeout = null;
    /**
     * Determines whether the current environment is a browser or not
     */
    this.isBrowser = typeof window !== 'undefined';
    /**
     * Transition end handler for the transitioning element
     */
    this.handleTransitionEnd = this.transitionEnd.bind(this);
    /**
     * Click handler for the dismiss button
     */
    this.handleDismiss = this.dismissEl.bind(this);
    this.transitionState = undefined;
    this.transitionPhase = toastConstants.TRANSITION_PHASE.BASE;
    this.inline = false;
    this.duration = undefined;
    this.actions = '';
    this.text = '';
    this.header = '';
    this.tag = '';
    this.timestamp = '';
    this.position = 'top-right';
    this.variant = 'info';
    this.transition = 'fade';
    this.show = true;
  }
  /**
   * Handles validating the position attribute
   */
  validatePosition(position) {
    if (!toastValidator.position(position)) {
      this.logger.error.attribute.position(`Given invalid value "${position}".`);
    }
  }
  /**
   * Handles validating the variant attribute
   */
  validateVariant(variant) {
    if (!toastValidator.variant(variant)) {
      this.logger.error.attribute.variant(`Given invalid value "${variant}".`);
    }
  }
  /**
   * Handles validating the transition attribute
   */
  validateTransition(transition) {
    if (!toastValidator.transition(transition)) {
      this.logger.error.attribute.transition(`Given invalid value "${transition}".`);
    }
  }
  /**
   * Handles updating the transition state based on the show attribute
   */
  changeShow(show) {
    if (show) {
      if (this.transitionState !== toastConstants.TRANSITION_STATE.ENTERING &&
        this.transitionState !== toastConstants.TRANSITION_STATE.ENTERED) {
        if (this.transition !== 'none') {
          this.setTransitionStateAndEmitEvent(toastConstants.TRANSITION_STATE.ENTERING);
        }
        else {
          this.setTransitionStateAndEmitEvent(toastConstants.TRANSITION_STATE.ENTERED);
        }
      }
    }
    else if (this.transitionState === toastConstants.TRANSITION_STATE.ENTERING ||
      this.transitionState === toastConstants.TRANSITION_STATE.ENTERED) {
      if (this.transition !== 'none') {
        this.setTransitionStateAndEmitEvent(toastConstants.TRANSITION_STATE.EXITING);
      }
      else {
        this.setTransitionStateAndEmitEvent(toastConstants.TRANSITION_STATE.EXITED);
      }
    }
  }
  /**
   * Dismiss the toast and emit the dismiss event.
   */
  async dismissEl() {
    if (this.durationTimeout) {
      clearTimeout(this.durationTimeout);
    }
    this.show = false;
    emitEvent(this, 'clDismiss', { component }, null);
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Validate the position attribute;
   * - Validate the variant attribute;
   * - Validate the transition attribute;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'dismiss', 'clDismiss');
    logDeprecatedEventOnce(this.logger, 'enter', 'clEnterStart');
    logDeprecatedEventOnce(this.logger, 'entered', 'clEnterEnd');
    logDeprecatedEventOnce(this.logger, 'exit', 'clExitStart');
    logDeprecatedEventOnce(this.logger, 'exited', 'clExitEnd');
    this.validatePosition(this.position);
    this.validateVariant(this.variant);
    this.validateTransition(this.transition);
  }
  /**
   * Just once before the component has fully loaded and rendered:
   * - Validate the position attribute;
   * - Update the transition state based on the show attribute;
   */
  componentWillLoad() {
    this.validatePosition(this.position);
    if (this.show) {
      if (this.transition !== 'none') {
        this.setTransitionStateAndEmitEvent(toastConstants.TRANSITION_STATE.ENTERING);
      }
      else {
        this.setTransitionStateAndEmitEvent(toastConstants.TRANSITION_STATE.ENTERED);
      }
    }
    else {
      this.setTransitionStateAndEmitEvent(toastConstants.TRANSITION_STATE.EXITED);
    }
  }
  /**
   * Just once after the component has fully loaded and rendered:
   * - Set the duration timeout;
   */
  componentDidLoad() {
    if (this.duration) {
      this.durationTimeout = window.setTimeout(this.handleDismiss, this.duration);
    }
  }
  /**
   * After every render:
   * - Update the transition state based on the transition phase
   */
  componentDidRender() {
    if ((this.transitionState === toastConstants.TRANSITION_STATE.ENTERING ||
      this.transitionState === toastConstants.TRANSITION_STATE.EXITING) &&
      this.transitionPhase === toastConstants.TRANSITION_PHASE.BASE) {
      if (this.isBrowser) {
        this.transitionPhase = toastConstants.TRANSITION_PHASE.ACTIVE;
      }
      else {
        /**
         * In order to have the transitioning element reset to the first step of the transition,
         * we have to request an animation frame and at the callback set the transition phase to active. This ensures
         * that the transitiong element is being rendered with the base transition phase.
         */
        window.requestAnimationFrame(() => {
          this.transitionPhase = toastConstants.TRANSITION_PHASE.ACTIVE;
        });
      }
    }
  }
  emitTransitionStateChangeEvent(state) {
    switch (state) {
      case toastConstants.TRANSITION_STATE.ENTERING:
        emitEvent(this, 'clEnterStart', { component }, null);
        break;
      case toastConstants.TRANSITION_STATE.ENTERED:
        emitEvent(this, 'clEnterEnd', { component }, null);
        break;
      case toastConstants.TRANSITION_STATE.EXITING:
        emitEvent(this, 'clExitStart', { component }, null);
        break;
      case toastConstants.TRANSITION_STATE.EXITED:
        emitEvent(this, 'clExitEnd', { component }, null);
        break;
    }
  }
  setTransitionState(state) {
    this.transitionPhase = toastConstants.TRANSITION_PHASE.BASE;
    this.transitionState = state;
  }
  setTransitionStateAndEmitEvent(state) {
    if (this.transitionState !== state) {
      this.emitTransitionStateChangeEvent(state);
      this.setTransitionState(state);
    }
  }
  getToastContainer() {
    const tagEl = this.tag && (h("cl-typography", { class: 'toast__tag', surface: 'light', variant: 'microcopy' }, this.tag));
    const timestampEl = this.timestamp && (h("cl-typography", { class: 'toast__timestamp', surface: 'light', variant: 'microcopy' }, this.timestamp));
    const headerEl = this.header && (h("cl-typography", { class: 'toast__header', variant: 'subtitle-2' }, this.header.toUpperCase()));
    // We only show the top element when there is either a tag and/or a header given.
    const showTopElement = !!(tagEl || headerEl);
    return (h("div", { class: cl('toast__container', { 'toast__container--extra-spacing': showTopElement }) }, showTopElement && (h("div", { class: 'toast__top' }, tagEl || headerEl, timestampEl)), tagEl && headerEl, h("cl-typography", { class: 'toast__text', surface: 'light' }, this.text), h("slot", { name: 'actions' }), this.actions && h("div", { class: 'toast__actions', innerHTML: this.actions })));
  }
  /**
   * Handler for ending animations from a specific transition state.
   */
  transitionEnd(e) {
    this.transitionPhase = toastConstants.TRANSITION_PHASE.DONE;
    if (e.currentTarget.classList.contains(`toast__content--${toastConstants.TRANSITION_STATE.EXITING}-${toastConstants.TRANSITION_PHASE.ACTIVE}`)) {
      this.setTransitionStateAndEmitEvent(toastConstants.TRANSITION_STATE.EXITED);
    }
    else if (e.currentTarget.classList.contains(`toast__content--${toastConstants.TRANSITION_STATE.ENTERING}-${toastConstants.TRANSITION_PHASE.ACTIVE}`)) {
      this.setTransitionStateAndEmitEvent(toastConstants.TRANSITION_STATE.ENTERED);
    }
  }
  render() {
    const { inline, position, transition, variant } = this;
    const transitionClasses = `toast__content--${this.transitionState}-${this.transitionPhase}`;
    return (h(Host, { class: cl(`toast toast--${variant}`, { 'toast--inline': inline, [`toast--${position}`]: !inline }) }, h("div", { class: cl(`background-main toast__content toast__content--${variant} ${transitionClasses}`, {
        [`toast__content--${transition}`]: transition !== 'none',
      }), onTransitionEnd: this.handleTransitionEnd }, h("slot", { name: 'icon' }), h("cl-icon", { name: cl(variant === 'info' ? 'generic--info' : `notification--${variant}`), slot: 'icon' }), this.getToastContainer(), h("cl-icon", { class: 'toast__close', name: 'action--close', onClick: this.handleDismiss }))));
  }
  static get watchers() { return {
    "position": ["validatePosition"],
    "variant": ["validateVariant"],
    "transition": ["validateTransition"],
    "show": ["changeShow"]
  }; }
  static get style() { return clToastCss; }
}, [6, "cl-toast", {
    "inline": [4],
    "duration": [2],
    "actions": [1],
    "text": [1],
    "header": [1],
    "tag": [1],
    "timestamp": [1],
    "position": [1],
    "variant": [1],
    "transition": [1],
    "show": [1028],
    "transitionState": [32],
    "transitionPhase": [32],
    "dismissEl": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-toast", "cl-icon", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-toast":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClToast$1);
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const ClToast = ClToast$1;
const defineCustomElement = defineCustomElement$1;

export { ClToast, defineCustomElement };

//# sourceMappingURL=cl-toast.js.map